import { Link, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, mainSiteUrl } from '@/mocks/categories';
import { getArticleBySlug } from '@/mocks/articles';
import type { ArticleData } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<ArticleData | null>(null);
  const [activeId, setActiveId] = useState('');
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const found = getArticleBySlug(slug || '');
    if (found) {
      setArticle(found);
      setActiveId(found.tocItems[0]?.id || '');
      setNotFound(false);
    } else {
      setNotFound(true);
    }
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (!article) return;

    const handleScroll = () => {
      let current = article.tocItems[0]?.id || '';
      article.tocItems.forEach((item) => {
        const el = document.getElementById(item.id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120) {
            current = item.id;
          }
        }
      });
      setActiveId(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [article]);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 px-4 md:px-6 py-20 text-center">
          <div className="max-w-lg mx-auto">
            <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-foreground-100">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </span>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">Artikel nicht gefunden</h1>
            <p className="text-sm text-foreground-500 mb-6">
              Der gesuchte Artikel existiert leider nicht. Kehre zur Startseite zurück und entdecke unsere Guides.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary-500 text-white hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </span>
              Zurück zur Startseite
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm text-foreground-500">Lade Artikel...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-20">
        <div className="px-4 md:px-6 py-3">
          <div className="max-w-5xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-400" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors">Startseite</Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              <span className="text-foreground-600 font-medium">{article.category}</span>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              <span className="text-foreground-500 truncate max-w-[200px]">{article.title}</span>
            </nav>
          </div>
        </div>

        <div className="px-4 md:px-6 py-6 md:py-10">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              <aside className="hidden lg:block w-56 flex-shrink-0">
                <div className="sticky top-24">
                  <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wide mb-3">
                    Inhaltsverzeichnis
                  </h4>
                  <nav className="space-y-0.5">
                    {article.tocItems.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        className={`block w-full text-left text-xs py-1.5 px-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                          activeId === item.id
                            ? 'text-primary-500 font-medium bg-primary-50'
                            : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
                        }`}
                      >
                        {item.title}
                      </button>
                    ))}
                  </nav>
                  <div className="mt-6 pt-4 border-t border-background-200">
                    <Link
                      to="/"
                      className="flex items-center gap-2 text-xs text-primary-500 hover:text-primary-600 font-medium transition-colors"
                    >
                      <span className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-arrow-left-line"></i>
                      </span>
                      Zur Startseite
                    </Link>
                  </div>
                </div>
              </aside>

              <article className="flex-1 min-w-0">
                <div className="mb-6 rounded-xl overflow-hidden h-48 md:h-64">
                  <img
                    src={article.heroImage}
                    alt={article.heroImageAlt}
                    className="w-full h-full object-cover"
                  />
                </div>

                <header className="mb-8">
                  <span className="inline-block px-3 py-1 rounded-full bg-foreground-100 text-foreground-600 text-xs font-medium mb-3">
                    {article.category} · {article.readTime} Lesezeit
                  </span>
                  <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight">
                    {article.title}
                  </h1>
                  <p className="mt-3 text-sm text-foreground-500 leading-relaxed max-w-2xl">
                    {article.description}
                  </p>
                  <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-foreground-400">
                    <span>Aktualisiert: Juli 2026</span>
                    <span>·</span>
                    <span>Von der oopbuyspread.lol Redaktion</span>
                  </div>
                </header>

                <div className="prose-custom space-y-10">
                  {article.sections.map((section) => (
                    <section key={section.id} id={section.id}>
                      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                        {section.title}
                      </h2>

                      {section.paragraphs.length > 0 &&
                        section.paragraphs.map((p, idx) => (
                          <p key={idx} className="text-sm text-foreground-600 leading-relaxed mb-3">
                            {p}
                          </p>
                        ))}

                      {section.listItems && section.listItems.length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                          {section.listItems.map((item, idx) => (
                            <div key={idx} className="flex items-start gap-3 p-4 bg-background-100 rounded-lg">
                              <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 text-accent-500">
                                <i className="ri-checkbox-circle-fill"></i>
                              </span>
                              <div>
                                {item.title && (
                                  <h4 className="text-sm font-semibold text-foreground-800">{item.title}</h4>
                                )}
                                <p className="text-xs text-foreground-500 mt-0.5 leading-relaxed">{item.text}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {section.numberedItems && section.numberedItems.length > 0 && (
                        <div className="space-y-3 mt-3">
                          {section.numberedItems.map((item, idx) => (
                            <div key={idx} className="flex items-start gap-3">
                              <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 rounded-full bg-foreground-100 text-foreground-600 text-xs font-bold">
                                {item.num || idx + 1}
                              </span>
                              <div className="flex-1">
                                {item.title && (
                                  <h4 className="text-sm font-semibold text-foreground-800">{item.title}</h4>
                                )}
                                <p className="text-sm text-foreground-600 leading-relaxed">{item.text}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {section.tableData && (
                        <div className="mt-4 overflow-x-auto">
                          <table className="w-full text-sm border-collapse">
                            <thead>
                              <tr className="bg-background-100">
                                {section.tableData.headers.map((h, idx) => (
                                  <th key={idx} className="text-left px-4 py-2.5 text-xs font-semibold text-foreground-700 whitespace-nowrap">
                                    {h}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {section.tableData.rows.map((row, rIdx) => (
                                <tr key={rIdx} className="border-t border-background-200">
                                  {row.map((cell, cIdx) => (
                                    <td key={cIdx} className={`px-4 py-2.5 text-xs ${cIdx === 0 ? 'font-medium text-foreground-700' : 'text-foreground-500'}`}>
                                      {cell}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {section.id === 'kategorien' && (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-4">
                          {categories.map((cat) => (
                            <a
                              key={cat.name}
                              href={cat.url}
                              target="_blank"
                              rel="noopener nofollow"
                              className="flex items-center gap-2.5 px-3 py-2.5 bg-background-100 rounded-lg text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                            >
                              <span className="w-5 h-5 flex items-center justify-center">
                                <i className={`${cat.icon} text-sm`}></i>
                              </span>
                              <span className="whitespace-nowrap text-xs">{cat.name}</span>
                            </a>
                          ))}
                        </div>
                      )}
                    </section>
                  ))}

                  {article.faqItems.length > 0 && (
                    <section id="faq">
                      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                        Häufig gestellte Fragen
                      </h2>
                      <div className="space-y-3">
                        {article.faqItems.map((faq, idx) => (
                          <div key={idx} className="bg-background-100 rounded-lg p-4">
                            <h4 className="text-sm font-semibold text-foreground-800 mb-1">{faq.q}</h4>
                            <p className="text-xs text-foreground-500 leading-relaxed">{faq.a}</p>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  {article.conclusion && (
                    <section id="fazit" className="bg-background-100 rounded-xl p-6 md:p-8">
                      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                        Fazit
                      </h2>
                      <p className="text-sm text-foreground-600 leading-relaxed">{article.conclusion}</p>
                      <div className="flex flex-col sm:flex-row gap-3 mt-6">
                        <a
                          href={mainSiteUrl}
                          target="_blank"
                          rel="noopener nofollow"
                          className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary-500 text-white hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap text-center"
                        >
                          OOPBuy Spreadsheet &rarr;
                        </a>
                        <a
                          href={mainSiteUrl}
                          target="_blank"
                          rel="noopener nofollow"
                          className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-accent-500 text-white hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap text-center"
                        >
                          Buy It Now &rarr;
                        </a>
                        <Link
                          to="/"
                          className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-background-200 text-foreground-700 hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap text-center"
                        >
                          Zurück zur Startseite
                        </Link>
                      </div>
                    </section>
                  )}
                </div>
              </article>
            </div>
          </div>
        </div>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.description,
            image: article.heroImage,
            author: { '@type': 'Organization', name: 'oopbuyspread.lol' },
            datePublished: '2026-07-15',
            dateModified: '2026-07-15',
            inLanguage: 'de',
            publisher: {
              '@type': 'Organization',
              name: 'oopbuyspread.lol',
              url: import.meta.env.VITE_SITE_URL || 'https://oopbuyspread.lol',
              logo: {
                '@type': 'ImageObject',
                url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/5133451b25e408e4a25e4df97e05add8.jpeg',
              },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `${import.meta.env.VITE_SITE_URL || 'https://oopbuyspread.lol'}/artikel/${article.slug}`,
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Startseite',
                item: `${import.meta.env.VITE_SITE_URL || 'https://oopbuyspread.lol'}/`,
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: article.category,
                item: `${import.meta.env.VITE_SITE_URL || 'https://oopbuyspread.lol'}/`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: article.title,
                item: `${import.meta.env.VITE_SITE_URL || 'https://oopbuyspread.lol'}/artikel/${article.slug}`,
              },
            ],
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: article.faqItems.map((faq) => ({
              '@type': 'Question',
              name: faq.q,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.a,
              },
            })),
          }),
        }}
      />
    </div>
  );
}